import { InvalidConfigurationError } from './errors.js';
/**
 * Validates basic experiment properties.
 */
function validateExperimentBasics(experiment) {
    if (!experiment.id) {
        throw new InvalidConfigurationError('experiment must have an id');
    }
    if (!experiment.condition_expression) {
        throw new InvalidConfigurationError(`experiment ${experiment.id} must have a condition_expression`);
    }
}
/**
 * Validates an individual experiment arm.
 */
function validateArm(arm) {
    if (!arm.id) {
        throw new InvalidConfigurationError(`arm must have an id`);
    }
    if (typeof arm.weight !== 'number') {
        throw new InvalidConfigurationError(`arm ${arm.id} must have a weight`);
    }
    if (arm.weight < 0) {
        throw new InvalidConfigurationError(`arm ${arm.id} must have a non-negative weight`);
    }
}
/**
 * Validates experiment arm meta data.
 */
function validateArmMeta(arm, experimentId) {
    if (arm.meta) {
        arm.meta.forEach((meta) => {
            if (!meta.type || typeof meta.value === 'undefined') {
                throw new InvalidConfigurationError(`arm ${arm.id} in experiment ${experimentId} has invalid meta data`);
            }
        });
    }
}
/**
 * Validates experiment disallow list configuration.
 */
function validateDisallowList(experiment, arms) {
    if (!experiment.meta)
        return;
    experiment.meta.forEach((meta) => {
        if (meta.type === 'arm_disallow_list') {
            const disallowList = meta.value;
            disallowList.forEach(entry => {
                // Check if referenced arm exists
                const armExists = arms.some(arm => arm.id === entry.id);
                if (!armExists) {
                    throw new InvalidConfigurationError(`experiment ${experiment.id} references non-existent arm '${entry.id}' in disallow list`);
                }
                // Validate disallow_list is an array of strings
                if (!Array.isArray(entry.disallow_list)) {
                    throw new InvalidConfigurationError(`arm ${entry.id} in experiment ${experiment.id} must have a valid disallow_list array`);
                }
            });
        }
    });
}
/**
 * Validates experiment arms configuration.
 */
function validateExperimentArms(experiment, arms) {
    if (!arms || arms.length === 0) {
        throw new InvalidConfigurationError(`experiment ${experiment.id} must have corresponding arms`);
    }
    arms.forEach(arm => {
        validateArm(arm);
        validateArmMeta(arm, experiment.id);
    });
}
/**
 * Validates the experiment configuration.
 * Checks for required fields and proper structure of experiments and their arms.
 *
 * @param {Experiment[]} experiments - Array of experiment configurations
 * @param {{ [key: string]: ConfigExperimentArm[] }} experimentArms - Map of experiment arms keyed by experiment ID
 * @throws {InvalidConfigurationError} If the configuration is invalid
 */
export function validateConfig(experiments, experimentArms) {
    if (!Array.isArray(experiments)) {
        throw new InvalidConfigurationError('experiments must be an array');
    }
    experiments.forEach(experiment => {
        validateExperimentBasics(experiment);
        const arms = experimentArms[experiment.id];
        validateExperimentArms(experiment, arms);
        validateDisallowList(experiment, arms);
    });
}
/**
 * Selects an experiment arm using weighted random selection.
 * Each arm has a weight that determines its probability of being selected.
 *
 * @param {ConfigExperimentArm[]} arms - Array of experiment arms with weights
 * @returns {ConfigExperimentArm | undefined} Selected arm or undefined if no valid arms
 */
export function calculateWeightedRandom(arms) {
    if (!arms.length)
        return undefined;
    const totalWeight = arms.reduce((sum, arm) => sum + arm.weight, 0);
    if (totalWeight <= 0)
        return undefined;
    const rand = Math.random() * totalWeight;
    let cumulative = 0;
    for (const arm of arms) {
        cumulative += arm.weight;
        if (rand < cumulative) {
            return arm;
        }
    }
    return arms[arms.length - 1];
}
